import axios from 'axios';
import { PlatformFeature, FeatureAccessLevel } from '../hooks/useFeatureAccess';
import { SERVICE_PORTS } from '../shared/constants';
import { freshStaffToken } from '@/adminHost';

const API_BASE_URL = import.meta.env.VITE_WEBSITE_API_BASE_URL ?? `http://localhost:${SERVICE_PORTS.API}`;

// Get auth token from localStorage
const getAuthToken = () => {
  // Try 'accessToken' first (standard), then 'token' (fallback)
  const token = freshStaffToken() || localStorage.getItem('accessToken') || localStorage.getItem('token');
  // Return null if token is the string "null" or "undefined"
  if (!token || token === 'null' || token === 'undefined') {
    console.warn('[FeatureAccessApi] No valid token found in localStorage');
    return null;
  }
  return token;
};

// Create axios instance with auth header
const createAxiosInstance = () => {
  const token = getAuthToken();
  if (!token) {
    console.warn('[FeatureAccessApi] No auth token found in localStorage');
  }
  const instance = axios.create({
    baseURL: `${API_BASE_URL}/api/feature-access`,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  
  // Add request interceptor to log requests
  instance.interceptors.request.use(
    (config) => {
      console.log('[FeatureAccessApi] Request:', config.method?.toUpperCase(), config.url, {
        hasToken: !!config.headers?.Authorization,
      });
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  // Add response interceptor to handle errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('[FeatureAccessApi] Unauthorized - token may be missing or invalid');
      }
      return Promise.reject(error);
    }
  );
  
  return instance;
};

export interface FeatureAccessResponse {
  feature: PlatformFeature;
  name: string;
  description: string;
  category: string;
  hasAccess: boolean;
  accessLevel: FeatureAccessLevel;
  source: 'ROLE_DEFAULT' | 'COMPANY' | 'USER_OVERRIDE' | 'SUPER_ADMIN';
  reason?: string;
  expiresAt?: Date;
}

export interface BulkFeatureAccessResponse {
  userId: string;
  companyId: string;
  role: string;
  features: FeatureAccessResponse[];
  checkedAt: Date;
}

export interface FeatureOverride {
  feature: PlatformFeature;
  accessLevel: FeatureAccessLevel;
  enabled: boolean;
  customConfig?: any;
}

export interface UpdateCompanyFeatureAccessDto {
  enabled: boolean;
  enabledFeatures: PlatformFeature[];
  featureOverrides?: FeatureOverride[];
  reason?: string;
}

export interface UserFeatureOverride {
  feature: PlatformFeature;
  accessLevel: FeatureAccessLevel;
  enabled: boolean;
  expiresAt?: Date;
  reason?: string;
}

export interface UpdateUserFeatureAccessDto {
  disabledFeatures?: PlatformFeature[];
  enabledFeatures?: PlatformFeature[];
  featureOverrides?: UserFeatureOverride[];
  useCustomAccess: boolean;
  reason?: string;
}

export interface FeatureCatalog {
  totalFeatures: number;
  categories: Record<string, {
    name: string;
    features: Array<{
      feature: PlatformFeature;
      name: string;
      description: string;
      route?: string;
      requiredRole?: string;
      dependencies?: PlatformFeature[];
    }>;
  }>;
}

/**
 * Feature Access API Service
 * Provides methods to interact with the feature access backend
 */
export const featureAccessApi = {
  /**
   * Get the complete feature catalog
   */
  async getFeatureCatalog(): Promise<FeatureCatalog> {
    const api = createAxiosInstance();
    const response = await api.get('/catalog');
    return response.data;
  },

  /**
   * Check if current user has access to a specific feature
   */
  async checkFeatureAccess(feature: PlatformFeature): Promise<FeatureAccessResponse> {
    const api = createAxiosInstance();
    const response = await api.get(`/check/${feature}`);
    return response.data;
  },

  /**
   * Check if current user can access a specific route
   */
  async checkRouteAccess(route: string): Promise<{ hasAccess: boolean; feature?: PlatformFeature }> {
    const api = createAxiosInstance();
    const response = await api.post('/check-route', { route });
    return response.data;
  },

  /**
   * Get all feature access for current user
   */
  async getMyFeatureAccess(): Promise<BulkFeatureAccessResponse> {
    const api = createAxiosInstance();
    const response = await api.get('/my-access');
    return response.data;
  },

  /**
   * Get feature access for a specific user (Admin only)
   */
  async getUserFeatureAccess(userId: string): Promise<BulkFeatureAccessResponse> {
    const api = createAxiosInstance();
    const response = await api.get(`/user/${userId}`);
    return response.data;
  },

  /**
   * Get company feature access settings (Admin only)
   */
  async getCompanyFeatureAccess(companyId: string): Promise<any> {
    const api = createAxiosInstance();
    const response = await api.get(`/company/${companyId}`);
    return response.data;
  },

  /**
   * Update company feature access (Super Admin only)
   */
  async updateCompanyFeatureAccess(
    companyId: string,
    updateDto: UpdateCompanyFeatureAccessDto
  ): Promise<any> {
    const api = createAxiosInstance();
    const response = await api.put(`/company/${companyId}`, updateDto);
    return response.data;
  },

  /**
   * Toggle a single feature for a company (Super Admin only)
   */
  async toggleCompanyFeature(
    companyId: string,
    feature: PlatformFeature,
    enabled: boolean,
    reason?: string
  ): Promise<{ message: string }> {
    const api = createAxiosInstance();
    const response = await api.post(`/company/${companyId}/toggle/${feature}`, {
      enabled,
      reason,
    });
    return response.data;
  },

  /**
   * Bulk update features for a company (Super Admin only)
   */
  async bulkUpdateCompanyFeatures(
    companyId: string,
    features: PlatformFeature[],
    enabled: boolean,
    accessLevel?: FeatureAccessLevel,
    reason?: string
  ): Promise<{ message: string; updatedCount: number }> {
    const api = createAxiosInstance();
    const response = await api.post(`/company/${companyId}/bulk-update`, {
      features,
      enabled,
      accessLevel,
      reason,
    });
    return response.data;
  },

  /**
   * Update user feature access (Admin only)
   */
  async updateUserFeatureAccess(
    userId: string,
    updateDto: UpdateUserFeatureAccessDto
  ): Promise<any> {
    const api = createAxiosInstance();
    const response = await api.put(`/user/${userId}`, updateDto);
    return response.data;
  },

  /**
   * Get user feature access settings (Admin only)
   */
  async getUserFeatureSettings(userId: string): Promise<any> {
    const api = createAxiosInstance();
    const response = await api.get(`/user/${userId}/settings`);
    return response.data;
  },

  /**
   * Initialize default features for a company (Super Admin only)
   */
  async initializeCompanyFeatures(
    companyId: string,
    planType?: string
  ): Promise<{ message: string }> {
    const api = createAxiosInstance();
    const response = await api.post(`/company/${companyId}/initialize`, {
      planType,
    });
    return response.data;
  },

  /**
   * Get feature access audit logs (Admin only)
   */
  async getFeatureAccessAuditLogs(
    companyId: string,
    params?: {
      feature?: PlatformFeature;
      startDate?: Date;
      endDate?: Date;
      page?: number;
      limit?: number;
    }
  ): Promise<{
    logs: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const api = createAxiosInstance();
    const response = await api.get(`/company/${companyId}/audit-logs`, { params });
    return response.data;
  },

  /**
   * Reset company features to basic/default configuration
   */
  async resetCompanyToBasic(companyId: string): Promise<{ message: string }> {
    const api = createAxiosInstance();
    const response = await api.post(`/company/${companyId}/reset-basic`, {});
    return response.data;
  },

  /**
   * Reset user features to basic/default configuration
   */
  async resetUserToBasic(userId: string): Promise<{ message: string }> {
    const api = createAxiosInstance();
    const response = await api.post(`/user/${userId}/reset-basic`, {});
    return response.data;
  },
};

